import { z } from "zod";

export const ProjectRole = z.enum(["owner", "editor", "viewer"]);
export type ProjectRole = z.infer<typeof ProjectRole>;

export const ContainerState = z.enum(["none", "creating", "running", "stopped", "error"]);
export type ContainerState = z.infer<typeof ContainerState>;

export const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$/;
export const slug = z.string().regex(SLUG_RE, "slug must be 3-40 chars: lowercase letters, digits, hyphens");

/** 0 = unlimited (the container may use everything the server has). */
export const ResourceLimits = z.object({
  /** CPU cores (fractional allowed); 0 = unlimited. */
  cpus: z.union([z.literal(0), z.number().min(0.25).max(256)]).default(2),
  /** Memory in MiB; 0 = unlimited. */
  memoryMb: z.union([z.literal(0), z.number().int().min(256).max(4_194_304)]).default(2048),
  /** Max processes; 0 = unlimited. */
  pids: z.union([z.literal(0), z.number().int().min(64).max(1_000_000)]).default(512),
});
export const isUnlimited = (l: { cpus: number; memoryMb: number; pids: number }) => l.cpus === 0 && l.memoryMb === 0 && l.pids === 0;
export type ResourceLimits = z.infer<typeof ResourceLimits>;

export const Project = z.object({
  id: z.string(),
  slug,
  name: z.string().min(1).max(80),
  description: z.string().max(500),
  ownerId: z.string(),
  image: z.string(),
  limits: ResourceLimits,
  state: ContainerState,
  stateMessage: z.string().nullable(),
  agentAccountId: z.string().nullable(),
  memoryDefault: z.boolean(),
  idleStopMinutes: z.number(),
  /** Developer mode: passwordless sudo and the usual container capabilities inside this project's container. */
  devMode: z.boolean(),
  /** "personal": the user's own hidden workspace behind the Chat section (one per user, not listed with projects). */
  kind: z.enum(["project", "personal"]),
  /** Lists only: chats running now, and chats that ended in an error nobody has opened since (sidebar dots). */
  activity: z.object({ running: z.number(), error: z.number() }).optional(),
  myRole: ProjectRole,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Project = z.infer<typeof Project>;

export const CreateProjectInput = z.object({
  name: z.string().trim().min(1).max(80),
  slug: slug.optional(),
  description: z.string().trim().max(500).default(""),
  limits: ResourceLimits.partial().default({}),
});
export type CreateProjectInput = z.infer<typeof CreateProjectInput>;

export const UpdateProjectInput = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  description: z.string().trim().max(500).optional(),
  limits: ResourceLimits.partial().optional(),
  /** null detaches the account. */
  agentAccountId: z.string().nullable().optional(),
  memoryDefault: z.boolean().optional(),
  /** Stop the container after this many idle minutes (no running chat, no chat activity); 0 = never. */
  idleStopMinutes: z.number().int().min(0).max(10080).optional(),
  devMode: z.boolean().optional(),
});
export type UpdateProjectInput = z.infer<typeof UpdateProjectInput>;

export const ProjectMember = z.object({
  userId: z.string(),
  email: z.email(),
  name: z.string(),
  /** the member's photo (data: image), shown beside their messages */
  avatar: z.string().nullable(),
  role: ProjectRole,
  addedAt: z.string(),
});
export type ProjectMember = z.infer<typeof ProjectMember>;

export const AddMemberInput = z.object({ email: z.email().transform((v) => v.trim().toLowerCase()), role: z.enum(["editor", "viewer"]).default("editor") });
export const SetMemberRoleInput = z.object({ role: z.enum(["editor", "viewer"]) });

export const SECRET_KEY_RE = /^[A-Z_][A-Z0-9_]{0,63}$/;
export const ProjectSecretMeta = z.object({ key: z.string(), updatedAt: z.string(), updatedBy: z.string().nullable() });
export type ProjectSecretMeta = z.infer<typeof ProjectSecretMeta>;
/** Bulk set (from a .env import): keys are validated like single secrets; existing keys are overwritten. */
export const ImportSecretsInput = z.object({ entries: z.array(z.object({ key: z.string().regex(SECRET_KEY_RE), value: z.string().max(16384) })).min(1).max(500) });
export const ImportSecretsResult = z.object({ added: z.array(z.string()), replaced: z.array(z.string()) });
export type ImportSecretsResult = z.infer<typeof ImportSecretsResult>;

export const SetSecretInput = z.object({
  key: z.string().regex(SECRET_KEY_RE, "UPPER_SNAKE_CASE, max 64 chars"),
  value: z.string().max(16384),
});

/** Live container facts, fetched on demand. */
export const ContainerInfo = z.object({
  state: ContainerState,
  containerId: z.string().nullable(),
  startedAt: z.string().nullable(),
  image: z.string().nullable(),
  cpuPercent: z.number().nullable(),
  memoryUsedMb: z.number().nullable(),
  memoryLimitMb: z.number().nullable(),
  pids: z.number().nullable(),
  ip: z.string().nullable(),
});
export type ContainerInfo = z.infer<typeof ContainerInfo>;

export const DockerHealth = z.object({
  ok: z.boolean(),
  version: z.string().nullable(),
  baseImage: z.string(),
  baseImagePresent: z.boolean(),
  error: z.string().nullable(),
});
export type DockerHealth = z.infer<typeof DockerHealth>;

/** One row of the Machines grid: project + live facts, fetched for all projects at once. */
export const ProjectOverview = z.object({
  project: Project,
  startedAt: z.string().nullable(),
  cpuPercent: z.number().nullable(),
  memoryUsedMb: z.number().nullable(),
  hostMemoryMb: z.number().nullable(),
  /** The container's real memory ceiling (its limit, or the server's memory when unlimited). */
  memoryLimitMb: z.number().nullable(),
  /** CPU cores the container may use (its limit, or every server core when unlimited). cpuPercent is Docker's:
   *  100 = one core, so the share of what the container may use is cpuPercent / cpuCores. */
  cpuCores: z.number().nullable(),
  ports: z.array(z.number()),
  chatCount: z.number(),
  lastChat: z.object({ id: z.string(), title: z.string(), model: z.string(), updatedAt: z.string() }).nullable(),
});
export type ProjectOverview = z.infer<typeof ProjectOverview>;

// ---------------- snapshots ----------------

export const ProjectSnapshot = z.object({
  id: z.string(),
  projectId: z.string(),
  label: z.string(),
  status: z.enum(["creating", "ready", "failed"]),
  sizeBytes: z.number(),
  rawBytes: z.number(),
  error: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string(),
  createdAt: z.string(),
});
export type ProjectSnapshot = z.infer<typeof ProjectSnapshot>;
export const CreateSnapshotInput = z.object({ label: z.string().trim().max(80).default("") });

// ---------------- health center ----------------

export const DiskUsage = z.object({ mount: z.string(), usedMb: z.number().nullable(), availMb: z.number().nullable(), totalMb: z.number().nullable() });
export const ProjectHealth = z.object({
  container: ContainerInfo,
  uptimeSec: z.number().nullable(),
  limits: ResourceLimits,
  /** See ProjectOverview.cpuCores. */
  cpuCores: z.number().nullable(),
  disks: z.array(DiskUsage),
  workspaceMb: z.number().nullable(),
  network: z.object({
    name: z.string(),
    ip: z.string().nullable(),
    gatewayPort: z.number().nullable(),
    /** Can the container reach the internet (HTTPS to a well-known host)? */
    internet: z.enum(["ok", "fail", "unknown"]),
    listeningPorts: z.array(z.number()),
  }),
  tools: z.array(z.object({ name: z.string(), version: z.string().nullable() })),
  auth: z.object({ accountMounted: z.boolean(), credentialsPresent: z.boolean() }),
  checkedAt: z.string(),
});
export type ProjectHealth = z.infer<typeof ProjectHealth>;

// ---------------- import ----------------

export const ImportSkip = z.object({ path: z.string(), reason: z.enum(["dependencies", "build", "vcs", "secret", "large", "system", "unsafe"]), bytes: z.number() });
export const ImportPlan = z.object({
  id: z.string(),
  projectId: z.string(),
  source: z.enum(["upload", "zip"]),
  /** Top-level folder that was stripped from every path, if the upload was a single folder. */
  root: z.string().nullable(),
  fileCount: z.number(),
  totalBytes: z.number(),
  topLevel: z.array(z.object({ name: z.string(), dir: z.boolean(), bytes: z.number() })),
  stack: z.array(z.string()),
  skipped: z.array(ImportSkip),
  warnings: z.array(z.string()),
  /** Whether /workspace already has files (then the user picks merge vs replace). */
  workspaceHasFiles: z.boolean(),
  expiresAt: z.string(),
});
export type ImportPlan = z.infer<typeof ImportPlan>;
export const ApplyImportInput = z.object({
  mode: z.enum(["merge", "replace"]).default("merge"),
  /** Paths from `skipped` the user wants anyway (e.g. an .env file). */
  include: z.array(z.string()).max(500).default([]),
});
export const CloneInput = z.object({
  url: z.string().trim().min(8).max(500),
  branch: z.string().trim().max(200).optional(),
  mode: z.enum(["merge", "replace"]).default("merge"),
});

export const GithubStatus = z.object({ configured: z.boolean(), source: z.enum(["env", "settings"]).nullable().default(null), clientId: z.string().nullable().default(null), callbackUrl: z.string().default(""), connected: z.boolean(), login: z.string().nullable(), scopes: z.string().nullable() });
export const GithubAppInput = z.object({ clientId: z.string().trim().min(1).max(100), clientSecret: z.string().trim().min(1).max(200) });
export type GithubStatus = z.infer<typeof GithubStatus>;
export const GithubOrg = z.object({ login: z.string(), avatarUrl: z.string().nullable() });
export type GithubOrg = z.infer<typeof GithubOrg>;
export const RepoName = z.string().trim().min(1).max(100).regex(/^[A-Za-z0-9._-]+$/, "letters, digits, . _ - only");
export const BranchName = z.string().trim().min(1).max(200).regex(/^(?!-)(?!.*\.\.)[^\s~^:?*\[\\]+$/, "invalid branch name");
export const GithubPublishInput = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("new"), name: RepoName, visibility: z.enum(["public", "private"]), owner: z.string().trim().max(100).nullable().default(null), description: z.string().trim().max(300).default("") }),
  z.object({ mode: z.literal("existing"), fullName: z.string().regex(/^[^/\s]+\/[^/\s]+$/), branch: BranchName, how: z.enum(["direct", "pull_request"]) }),
]);
export type GithubPublishInput = z.infer<typeof GithubPublishInput>;
export const ProjectRepoLink = z.object({
  fullName: z.string(),
  htmlUrl: z.string(),
  branch: z.string(),
  how: z.enum(["direct", "pull_request"]),
  headBranch: z.string().nullable(),
  pr: z.object({ number: z.number(), url: z.string(), state: z.enum(["open", "merged", "closed"]) }).nullable(),
  lastPushAt: z.string().nullable(),
});
export type ProjectRepoLink = z.infer<typeof ProjectRepoLink>;
export const ProjectGithubStatus = z.object({
  connected: z.boolean(),
  login: z.string().nullable(),
  link: ProjectRepoLink.nullable(),
  /** Workspace git facts. */
  git: z.object({ initialized: z.boolean(), branch: z.string().nullable(), dirty: z.boolean(), commits: z.number() }),
});
export type ProjectGithubStatus = z.infer<typeof ProjectGithubStatus>;
export const GithubPushInput = z.object({ message: z.string().trim().max(200).default("") });
export const GithubRepo = z.object({ fullName: z.string(), private: z.boolean(), defaultBranch: z.string(), description: z.string().nullable(), updatedAt: z.string(), cloneUrl: z.string() });
export type GithubRepo = z.infer<typeof GithubRepo>;

export const UpdatePhase = z.enum(["running", "rolling_back", "done", "failed", "rolled_back"]);
export const UpdateState = z.object({
  phase: UpdatePhase,
  percent: z.number(),
  step: z.string(),
  from: z.object({ commit: z.string().nullable(), tag: z.string().nullable() }),
  to: z.object({ tag: z.string() }),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  error: z.string().nullable(),
  log: z.string(),
});
export type UpdateState = z.infer<typeof UpdateState>;
export const UpdatesInfo = z.object({
  /** The release tag this checkout is on, else the package version. */
  version: z.string(),
  commit: z.string().nullable(),
  branch: z.string().nullable(),
  node: z.string(),
  baseImage: z.string(),
  /** owner/repo of the GitHub origin, when there is one. */
  repo: z.string().nullable(),
  hasToken: z.boolean(),
  latest: z.object({ tag: z.string(), name: z.string(), notes: z.string().nullable(), url: z.string().nullable(), publishedAt: z.string().nullable(), newer: z.boolean(), commitsBehind: z.number().nullable() }).nullable(),
  checkedAt: z.string(),
  warning: z.string().nullable(),
  updateState: UpdateState.nullable(),
  supervised: z.boolean(),
});
export type UpdatesInfo = z.infer<typeof UpdatesInfo>;

// ---------------- services (programs kept running in a project's container) ----------------

/** A program the container keeps running (a published app, its helpers), in /workspace/.codvero/services.json. */
export const ServiceSpec = z.object({
  name: z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/, "lowercase letters, digits and -"),
  command: z.string().trim().min(1).max(2000),
  cwd: z.string().trim().max(500).regex(/^\/workspace(\/|$)/, "a folder under /workspace").default("/workspace"),
  env: z.record(z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/), z.string().max(4000)).default({}),
  /** the port it serves; it is checked and the program restarted when it stops answering */
  port: z.number().int().min(1).max(65535).nullable().default(null),
  enabled: z.boolean().default(true),
});
export type ServiceSpec = z.infer<typeof ServiceSpec>;
export const SaveServicesInput = z.object({ services: z.array(ServiceSpec).max(20) });
export type SaveServicesInput = z.infer<typeof SaveServicesInput>;
export interface ServiceStatus extends ServiceSpec {
  status: "running" | "starting" | "restarting" | "stopped" | "crashed";
  pid: number | null;
  startedAt: string | null;
  /** exits in the last two minutes */
  restarts: number;
  lastExit: { code: number | null; signal: string | null; at: string } | null;
}
export interface ServicesOverview {
  /** whether the container's supervisor is running (false: the project is stopped, or it just started) */
  supervisor: boolean;
  services: ServiceStatus[];
}
